import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { PrivateKeyRecoveryOptionStatusEnum } from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconAlert } from '#src/atoms/icons/system/iconAlert.ts';
import { iconAlertCircle } from '#src/atoms/icons/system/iconAlertCircle.ts';
import { iconCheckCircle } from '#src/atoms/icons/system/iconCheckCircle.ts';
import { iconEmail } from '#src/atoms/icons/system/iconEmail.ts';
import { iconEye } from '#src/atoms/icons/system/iconEye.ts';
import { iconEyeOff } from '#src/atoms/icons/system/iconEyeOff.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, BodyS, HeadlineXS } from '#src/atoms/typography/index.tsx';
import {
  SecurityCheckTab,
  useAccountSecurityCheckContext,
} from '#src/contexts/accountSecurityCheck/index.ts';
import type { RecoveryOption } from '#src/hooks/recovery/useRecoveryOptions.ts';
import { useRecoveryOptions as useRecoveryEmails } from '#src/hooks/recovery/useRecoveryOptions.ts';
import { useResendVerificationCodeForRecoveryEmail } from '#src/hooks/recovery/useResendVerificationCodeForRecoveryEmail.ts';
import { useToggle } from '#src/hooks/useToggle.ts';
import { glossary } from '#src/lib/glossary.ts';
import { hideEmailDetails } from '#src/lib/privacy.ts';

const Email = styled(BodyM)`
  display: flex;
  gap: var(--unit);
  align-items: center;
`;

const EmailItem = ({ email }: { email: string }) => {
  const [show, toggleShow] = useToggle(false);

  return (
    <Email color="var(--c-white)">
      {show ? email : hideEmailDetails(email)}
      <button type="button" onClick={toggleShow}>
        <FontAwesomeIcon
          icon={show ? iconEyeOff : iconEye}
          size="xs"
          color="var(--c-white)"
        />
      </button>
    </Email>
  );
};

export const RecoveryEmail = () => {
  const { setOnBackTarget, setSecurityCheckTab, setUnverifiedRecoveryEmail } =
    useAccountSecurityCheckContext();
  const { resendVerificationCodeForRecoveryEmail } =
    useResendVerificationCodeForRecoveryEmail();
  const { recoveryEmails, allActiveRecoveryEmailsArePrivateRelay } =
    useRecoveryEmails();
  const hasRecoveryEmail = !!recoveryEmails.length;
  const hasActiveRecoveryEmail = recoveryEmails.find(
    option => option.status === PrivateKeyRecoveryOptionStatusEnum.ACTIVE
  );

  const resendCode = (option: RecoveryOption) => {
    resendVerificationCodeForRecoveryEmail(option.id);
    setOnBackTarget(SecurityCheckTab.HOME);
    setSecurityCheckTab(SecurityCheckTab.VERIFY_RECOVERY_EMAIL);
    setUnverifiedRecoveryEmail({
      destination: option.destination,
      recoveryOptionId: option.id,
    });
  };

  return (
    <Vertical>
      <Horizontal>
        {hasActiveRecoveryEmail ? (
          allActiveRecoveryEmailsArePrivateRelay ? (
            <FontAwesomeIcon icon={iconAlert} color="var(--c-yellow-500)" />
          ) : (
            <FontAwesomeIcon
              icon={iconCheckCircle}
              color="var(--c-onBase-success)"
            />
          )
        ) : (
          <FontAwesomeIcon icon={iconAlertCircle} color="var(--c-red-500)" />
        )}
        <HeadlineXS as="h6" color="var(--c-white)">
          <FormattedMessage
            defaultMessage="{nb, plural, one {Recovery email} other {Recovery emails}}"
            values={{ nb: recoveryEmails.length }}
          />
        </HeadlineXS>
      </Horizontal>
      {!hasRecoveryEmail && (
        <BodyS color="var(--c-nd-600)">
          <FormattedMessage defaultMessage="Add a recovery email to recover your Sorare wallet if you lose access to your account." />
        </BodyS>
      )}
      {!!recoveryEmails.length && (
        <Vertical gap={2}>
          {recoveryEmails.map(email => (
            <div key={email.id}>
              <EmailItem email={email.destination} />
              {email.status ===
                PrivateKeyRecoveryOptionStatusEnum.PENDING_VALIDATION && (
                <>
                  <BodyS color="var(--c-nd-600)">
                    <FormattedMessage
                      defaultMessage="{icon} Pending verification"
                      values={{
                        icon: (
                          <FontAwesomeIcon
                            icon={iconAlertCircle}
                            color="var(--c-red-500)"
                          />
                        ),
                      }}
                    />
                  </BodyS>
                  <BodyS color="var(--c-link)">
                    <button type="button" onClick={() => resendCode(email)}>
                      <FormattedMessage {...glossary.resendVerificationCode} />
                    </button>
                  </BodyS>
                </>
              )}
            </div>
          ))}
          {allActiveRecoveryEmailsArePrivateRelay ? (
            <BodyS color="var(--c-yellow-500)">
              <FormattedMessage defaultMessage="Your recovery email is an Apple Private Relay address, which can be deactivated by Apple at any time. Add a personal email to ensure you can always recover your wallet." />
            </BodyS>
          ) : null}
        </Vertical>
      )}

      <Button
        size="small"
        color={hasRecoveryEmail ? 'secondary' : 'primary'}
        onClick={() => {
          setSecurityCheckTab(SecurityCheckTab.ADD_RECOVERY_EMAIL);
          setUnverifiedRecoveryEmail(null);
        }}
        startIcon={<FontAwesomeIcon icon={iconEmail} />}
      >
        {hasRecoveryEmail ? (
          <FormattedMessage defaultMessage="Add another recovery email" />
        ) : (
          <FormattedMessage defaultMessage="Add recovery email" />
        )}
      </Button>
    </Vertical>
  );
};
